import { Timestamp } from "@firebase/firestore";
import { Step } from "../models/Recipe";

export function getMonthName(date: Date) {
  return date.toLocaleString("en-US", { month: "long" });
}

export const sumStepDays = (steps: Step[], currentStep: number) => {
  let sum = 0;
  for (let i = 0; i < steps.length; i++) {
    if (i - 1 === currentStep) {
      return sum;
    } else {
      sum += steps[i].duration;
    }
  }
};

export function addDaysToDate(date: Date, daysToAdd: number) {
  const result = new Date(date);
  result.setDate(result.getDate() + daysToAdd);
  return result;
}

export function calculateTimeLeft(targetDate: Date) {
  const now = new Date();
  const difference = targetDate.getTime() - now.getTime();

  let timeLeft = {};

  if (difference > 0) {
    timeLeft = {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }

  return timeLeft;
}

export const daysUntilDate = (date: Date) => {
  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  const timeDiff = date.getTime() - Date.now();
  const daysDiff = Math.floor(timeDiff / millisecondsPerDay);

  return daysDiff >= 0 ? daysDiff : -1;
};

export function getStartOfCurrentQuarter(): Date {
  const now = new Date();
  const currentMonth = now.getMonth();
  const quarterStartMonth = currentMonth - (currentMonth % 3);
  return new Date(now.getFullYear(), quarterStartMonth, 1);
}

export function getEndOfCurrentQuarter(): Date {
  const now = new Date();
  const currentMonth = now.getMonth();
  const quarterEndMonth = currentMonth - (currentMonth % 3) + 2;
  const lastDay = new Date(now.getFullYear(), quarterEndMonth + 1, 0).getDate(); // day 0 of next month = last day of current
  return new Date(now.getFullYear(), quarterEndMonth, lastDay, 23, 59, 59, 999);
}

export function toMillis(input: string | Date | Timestamp | undefined | null): number {
  if (!input) return 0;

  if (input instanceof Date) return input.getTime();

  if (typeof input === "string") {
    const parsed = new Date(input);
    return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  }

  if (typeof input === "object" && typeof (input as any).toDate === "function") {
    return (input as Timestamp).toDate().getTime();
  }

  return 0;
}

export function getQuarterForDate(date: Date): number {
  const month = date.getMonth(); // 0 = Jan, 11 = Dec
  return Math.floor(month / 3) + 1;
}

export function getQuarterDetails(date: Date): {
  quarter: number;
  start: Date;
  end: Date;
} {
  const month = date.getMonth(); // 0 = Jan
  const year = date.getFullYear();

  const quarter = Math.floor(month / 3) + 1;
  const startMonth = (quarter - 1) * 3;
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, startMonth + 3, 0, 23, 59, 59, 999); // Last day of quarter

  return { quarter, start, end };
}
export function formatQuarterHeader(date: Date): string {
  const { quarter, start, end } = getQuarterDetails(date);
  const startStr = start.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const endStr = end.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `Q${quarter} ${start.getFullYear()} (${startStr} – ${endStr})`;
}

export function getStartOfQuarter(date: Date): Date {
  const month = date.getMonth();
  const year = date.getFullYear();
  const startMonth = Math.floor(month / 3) * 3;
  return new Date(year, startMonth, 1);
}

export function getEndOfQuarter(date: Date): Date {
  const start = getStartOfQuarter(date);
  return new Date(start.getFullYear(), start.getMonth() + 3, 0, 23, 59, 59, 999);
}

export function isValidFirestoreDate(value: unknown): boolean {
  return (
    value instanceof Date ||
    (typeof value === "object" &&
      value !== null &&
      typeof (value as { toDate?: () => Date }).toDate === "function")
  );
}

export function getEndOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 11, 31, 23, 59, 59, 999);
}
export function getStartOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 0, 1, 0, 0, 0, 0);
}